import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, Zap, HelpCircle, Database, Waves, Users, Camera, UploadCloud, Loader2, ArrowRight, RotateCcw, Phone } from "lucide-react";
import { toast } from "sonner";
import Reveal from "./Reveal";
import SalesVideo from "./SalesVideo";
import UnitCard from "./UnitCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const enquiryInputCls = "rounded-2xl border-2 border-tpeco-ink px-4 py-3 font-semibold placeholder:text-neutral-400 focus:outline-none focus:ring-4 focus:ring-tpeco-blue/30";

const SIZE_MAP = {
  "electric-storage": { "1-2": ["25", "50"], "2-3": ["80"], "3-4": ["125"], "4-5": ["160"], "5+": ["160"] },
  "gas-storage": { "1-2": ["135"], "2-3": ["135"], "3-4": ["135", "130"], "4-5": ["170", "160"], "5+": ["170"] },
  "gas-continuous": { "1-2": ["16", "12"], "2-3": ["20", "16"], "3-4": ["26"], "4-5": ["26"], "5+": ["32"] },
  "electric-instantaneous": { "1-2": ["e13"], "2-3": ["e27"], "3-4": ["e27"], "4-5": ["e27"], "5+": ["e27"] },
  "gas-instantaneous": { "1-2": ["10"], "2-3": ["16"], "3-4": ["16"], "4-5": ["16"], "5+": ["16"] },
};

const STEPS = [
  {
    key: "fuel", question: "WHAT POWERS YOUR CURRENT UNIT?",
    options: [
      { value: "gas", label: "GAS", icon: Flame, hint: "Mains gas or LPG bottles" },
      { value: "electric", label: "ELECTRIC", icon: Zap, hint: "Wired or plug-in" },
      { value: "any", label: "NOT SURE", icon: HelpCircle, hint: "No worries — snap a photo instead" },
    ],
  },
  {
    key: "system", question: "STORAGE, CONTINUOUS OR INSTANTANEOUS?",
    options: [
      { value: "storage", label: "STORAGE TANK", icon: Database, hint: "Big cylinder that stores hot water" },
      { value: "continuous", label: "CONTINUOUS FLOW", icon: Waves, hint: "Gas wall box — endless hot water" },
      { value: "instantaneous", label: "INSTANTANEOUS", icon: Zap, hint: "Instant wall unit — electric or no-powerpoint gas" },
      { value: "any", label: "NOT SURE", icon: HelpCircle, hint: "We'll show popular picks" },
    ],
  },
  {
    key: "people", question: "HOW MANY PEOPLE AT HOME?",
    options: [
      { value: "1-2", label: "1-2", icon: Users },
      { value: "2-3", label: "2-3", icon: Users },
      { value: "3-4", label: "3-4", icon: Users },
      { value: "4-5", label: "4-5", icon: Users },
      { value: "5+", label: "5+", icon: Users },
    ],
  },
];

function recommend(units, answers) {
  const fuelKey = answers.fuel === "any" ? null : answers.fuel;
  const sysKey = answers.system === "any" ? null : answers.system;
  let pool = units.filter((x) => x.available);
  if (fuelKey && sysKey) {
    const sizes = SIZE_MAP[`${fuelKey}-${sysKey}`]?.[answers.people] || [];
    const exact = pool.filter((x) => x.fuel === fuelKey && x.system === sysKey && sizes.includes(x.size_class));
    if (exact.length) return exact.sort((a, b) => a.price - b.price).slice(0, 4);
  }
  if (fuelKey) pool = pool.filter((x) => x.fuel === fuelKey);
  if (sysKey) pool = pool.filter((x) => x.system === sysKey);
  return pool.sort((a, b) => a.price - b.price).slice(0, 4);
}

const MAX_BYTES = 7 * 1024 * 1024;
const MAX_DIM = 2048;

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ img, url });
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("decode failed")); };
    img.src = url;
  });
}

async function prepareImage(file) {
  if (file.size <= MAX_BYTES) return file;
  const { img, url } = await loadImage(file);
  try {
    const scale = Math.min(1, MAX_DIM / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    for (const q of [0.92, 0.8, 0.65]) {
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", q));
      if (blob && blob.size <= MAX_BYTES) return new File([blob], "unit.jpg", { type: "image/jpeg" });
    }
    throw new Error("could not compress");
  } finally {
    URL.revokeObjectURL(url);
  }
}

export default function UnitFinder({ units, onBookUnit, onPhotoIdentified }) {
  const [mode, setMode] = useState("photo");
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);
  const [photoState, setPhotoState] = useState({ status: "idle", result: null, preview: null });

  const pick = (key, value) => {
    const next = { ...answers, [key]: value };
    setAnswers(next);
    if (step < STEPS.length - 1) setStep(step + 1);
    else setDone(true);
  };

  const reset = () => { setStep(0); setAnswers({}); setDone(false); };
  const results = done ? recommend(units, answers) : [];

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoState({ status: "loading", result: null, preview: URL.createObjectURL(file) });
    try {
      const prepared = await prepareImage(file);
      const fd = new FormData();
      fd.append("file", prepared);
      const res = await axios.post(`${API}/identify-unit`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (res.data?.photo_id) onPhotoIdentified?.(res.data.photo_id);
      setPhotoState((s) => ({ ...s, status: "done", result: res.data }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't analyse that photo — try a clearer shot of the data plate.");
      setPhotoState({ status: "idle", result: null, preview: null });
    }
  };

  const id2 = photoState.result?.identification;
  const identified = !!id2?.brand;
  const onSite = identified && photoState.result?.matches?.length > 0;
  const [enquiry, setEnquiry] = useState({ name: "", phone: "", message: "" });
  const [sendingEnquiry, setSendingEnquiry] = useState(false);

  const submitEnquiry = async (e) => {
    e.preventDefault();
    setSendingEnquiry(true);
    try {
      const idSummary = `[Photo enquiry: ${id2?.brand || "unknown brand"}${id2?.model_guess ? " " + id2.model_guess : ""}]`;
      await axios.post(`${API}/leads`, {
        name: enquiry.name, phone: enquiry.phone, suburb: "",
        message: `${idSummary} ${enquiry.message}`.trim(), unit_id: null,
        photo_id: photoState.result?.photo_id || null,
      });
      toast.success("Got it! We'll take a look and call you back with a price.");
      setEnquiry({ name: "", phone: "", message: "" });
    } catch {
      toast.error("That didn't send — call us on 0475 776 555 instead.");
    } finally {
      setSendingEnquiry(false);
    }
  };

  const enquiryForm = (
    <form onSubmit={submitEnquiry} className="mt-5 grid gap-3" data-testid="photo-enquiry-form">
      <input required value={enquiry.name} onChange={(e) => setEnquiry({ ...enquiry, name: e.target.value })}
        placeholder="Name" data-testid="photo-enquiry-name" className={enquiryInputCls} />
      <input required value={enquiry.phone} onChange={(e) => setEnquiry({ ...enquiry, phone: e.target.value })}
        placeholder="Mobile number" data-testid="photo-enquiry-phone" className={enquiryInputCls} />
      <textarea value={enquiry.message} onChange={(e) => setEnquiry({ ...enquiry, message: e.target.value })} rows={2}
        placeholder="Anything we should know? (Unit type, access, urgency)" data-testid="photo-enquiry-message" className={enquiryInputCls} />
      <button type="submit" disabled={sendingEnquiry} className="btn-brutal w-full sm:w-auto" data-testid="photo-enquiry-submit">
        {sendingEnquiry ? "SENDING…" : "SEND ENQUIRY"} <ArrowRight className="w-5 h-5" />
      </button>
    </form>
  );

  return (
    <section id="finder" className="bg-tpeco-smoke border-y-4 border-black py-20 sm:py-28" data-testid="unit-finder-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <Reveal>
            <span className="chip-brutal bg-tpeco-blue border-tpeco-blue text-white">CHAPTER 01 — FIND YOUR UNIT</span>
            <h2 className="mt-4 font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9]" data-testid="finder-heading">
              WHICH UNIT IS<br /><span className="bg-tpeco-pink text-white px-3 rounded-2xl inline-block">ON YOUR WALL?</span>
            </h2>
            <p className="mt-4 max-w-2xl font-semibold text-neutral-700">
              Snap a photo of your unit's data plate — or answer 3 quick questions — and we'll match you to the right
              like-for-like swap with the price upfront. No email hostage-taking. No "call for a quote".
            </p>
          </Reveal>
          <Reveal i={1} className="hidden lg:block">
            <SalesVideo />
          </Reveal>
        </div>

        <Reveal i={1} className="mt-8 flex gap-3 flex-wrap">
          <button onClick={() => setMode("photo")} data-testid="finder-mode-photo"
            className={`${mode === "photo" ? "btn-brutal" : "btn-brutal-light !text-tpeco-ink"} !text-lg`}>
            <Camera className="w-5 h-5" /> SNAP A PHOTO
          </button>
          <button onClick={() => setMode("quiz")} data-testid="finder-mode-quiz"
            className={`${mode === "quiz" ? "btn-brutal" : "btn-brutal-light !text-tpeco-ink"} !text-lg`}>
            <HelpCircle className="w-5 h-5" /> ANSWER 3 QUESTIONS
          </button>
        </Reveal>

        {mode === "quiz" && (
          <div className="mt-10" data-testid="quiz-panel">
            <AnimatePresence mode="wait">
              {!done ? (
                <motion.div key={step} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.35 }}>
                  <div className="flex items-center gap-3 mb-6">
                    {STEPS.map((s, i) => (
                      <div key={s.key} className={`h-2 flex-1 rounded-full border border-tpeco-ink ${i <= step ? "bg-tpeco-pink" : "bg-white"}`} />
                    ))}
                  </div>
                  <h3 className="font-display uppercase text-3xl sm:text-4xl" data-testid={`quiz-question-${step}`}>
                    <span className="text-white bg-tpeco-blue px-3 rounded-xl mr-2 inline-block">0{step + 1}</span>{STEPS[step].question}
                  </h3>
                  <div className={`mt-6 grid gap-4 ${STEPS[step].options.length > 3 ? "grid-cols-2 sm:grid-cols-5" : "sm:grid-cols-3"}`}>
                    {STEPS[step].options.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => pick(STEPS[step].key, opt.value)}
                        data-testid={`quiz-option-${STEPS[step].key}-${opt.value}`}
                        className="card-brutal p-5 text-left hover:bg-tpeco-blue/10 hover:-translate-y-1 transition-[transform,background-color] duration-150 group"
                      >
                        <opt.icon className="w-8 h-8 mb-3" strokeWidth={2.25} />
                        <span className="block font-display text-2xl uppercase">{opt.label}</span>
                        {opt.hint && <span className="block mt-1 text-xs font-semibold text-neutral-600 group-hover:text-black">{opt.hint}</span>}
                      </button>
                    ))}
                  </div>
                </motion.div>
              ) : (
                <motion.div key="results" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} data-testid="quiz-results">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <h3 className="font-display uppercase text-3xl sm:text-5xl">YOUR MATCHES <span className="bg-tpeco-pink text-white px-3 rounded-2xl inline-block">— PRICED &amp; READY</span></h3>
                    <button onClick={reset} className="chip-brutal bg-white hover:bg-tpeco-blue/10 transition-colors" data-testid="quiz-reset-button">
                      <RotateCcw className="w-4 h-4" /> START OVER
                    </button>
                  </div>
                  {answers.fuel === "electric" && answers.system === "instantaneous" && (
                    <p className="mt-3 font-bold text-sm bg-tpeco-blue text-white inline-block px-3 py-1 rounded-full">Electric instant units need 3-phase power — we include the electrician.</p>
                  )}
                  <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {results.map((u, i) => <UnitCard key={u.id} unit={u} i={i} onBook={onBookUnit} compact />)}
                  </div>
                  {results.length === 0 && <p className="mt-6 font-bold">Loading the catalogue… one sec.</p>}
                </motion.div>
              )}
            </AnimatePresence>
            <div className="lg:hidden mt-10" data-testid="finder-video-mobile-quiz">
              <SalesVideo />
            </div>
          </div>
        )}

        {mode === "photo" && (
          <div className="mt-10" data-testid="photo-panel">
            <div className="grid lg:grid-cols-2 gap-8">
              <div>
                <label
                  htmlFor="unit-photo-input"
                  data-testid="photo-dropzone"
                  className="card-brutal flex flex-col items-center justify-center gap-3 min-h-[280px] cursor-pointer border-dashed !border-4 hover:bg-tpeco-blue/10 transition-colors p-6 text-center"
                >
                  {photoState.status === "loading" ? (
                    <>
                      <Loader2 className="w-10 h-10 animate-spin" />
                      <span className="font-display text-2xl uppercase">STUDYING YOUR UNIT…</span>
                    </>
                  ) : (
                    <>
                      <UploadCloud className="w-10 h-10" strokeWidth={2} />
                      <span className="font-display text-2xl uppercase">DROP OR TAP TO UPLOAD</span>
                      <span className="text-sm font-semibold text-neutral-600 max-w-sm">
                        Photo of your unit or its data plate. Electric storage: sticker wrapped around the top of the cylinder.
                        Gas storage: data plate low on the opposite side of the panel. Continuous flow: sticker on the side of the case.
                      </span>
                    </>
                  )}
                </label>
                <input id="unit-photo-input" data-testid="photo-input" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onPhoto} />
                {photoState.preview && (
                  <div className="mt-4" data-testid="photo-result-stack">
                    <img
                      src={photoState.preview}
                      alt="Your hot water unit"
                      className={`w-full max-h-56 object-contain border-[3px] border-tpeco-ink bg-white ${photoState.status === "done" && id2 ? "rounded-t-[1.75rem]" : "rounded-2xl"}`}
                      data-testid="photo-preview"
                    />
                    {photoState.status === "done" && id2 && (
                      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="card-brutal !rounded-t-none !border-t-0 p-6" data-testid="photo-result">
                        {identified && <span className="chip-brutal bg-tpeco-pink border-tpeco-pink text-white">SYSTEM IDENTIFIED</span>}
                        <h3 className={`mt-3 font-display uppercase text-3xl ${identified ? "" : "text-red-600"}`} data-testid="photo-result-brand">
                          {id2.brand || "BRAND UNKNOWN"} {id2.model_guess ? `· ${id2.model_guess}` : ""}
                        </h3>
                        {identified && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            <span className="chip-brutal bg-tpeco-blue border-tpeco-blue text-white">{(id2.fuel_type || "unknown").toUpperCase()}</span>
                            <span className="chip-brutal bg-tpeco-blue border-tpeco-blue text-white">{(id2.system_type || "unknown").replace("_", " ").toUpperCase()}</span>
                            {(id2.capacity_litres || id2.flow_lpm) && (
                              <span className="chip-brutal bg-tpeco-blue border-tpeco-blue text-white">{id2.capacity_litres ? `${id2.capacity_litres}L` : `${id2.flow_lpm}L/MIN`}</span>
                            )}
                            {!id2.capacity_litres && !id2.flow_lpm && Array.isArray(id2.capacity_range_litres) && id2.capacity_range_litres.length === 2 && (
                              <span className="chip-brutal bg-tpeco-blue border-tpeco-blue text-white">LIKELY {id2.capacity_range_litres[0]}–{id2.capacity_range_litres[1]}L</span>
                            )}
                            <span className="chip-brutal bg-white">CONFIDENCE: {(id2.confidence || "low").toUpperCase()}</span>
                          </div>
                        )}
                        {onSite ? (
                          <>
                            <p className="mt-4 font-semibold text-neutral-700" data-testid="photo-likely-text">
                              Based on the image provided, here are the units we have identified as likely to be your direct swap-out.
                            </p>
                            <div className="mt-4 grid sm:grid-cols-2 gap-4">
                              {photoState.result.matches.map((u, i) => <UnitCard key={u.id} unit={u} i={i} onBook={onBookUnit} compact />)}
                            </div>
                            {photoState.result.alternatives?.length > 0 && (
                              <>
                                <p className="mt-6 font-semibold text-neutral-700" data-testid="photo-alternatives-text">
                                  Here are additional units compatible to be swapped out.
                                </p>
                                <div className="mt-4 grid sm:grid-cols-2 gap-4">
                                  {photoState.result.alternatives.map((u, i) => <UnitCard key={u.id} unit={u} i={i} onBook={onBookUnit} compact />)}
                                </div>
                              </>
                            )}
                            <p className="mt-4 text-sm font-bold text-neutral-700" data-testid="photo-mention-note">
                              Mention the item number (e.g. {photoState.result.matches[0].id}) when booking.
                            </p>
                            <p className="mt-3 text-sm font-bold text-tpeco-blue bg-tpeco-blue/10 border-2 border-dashed border-tpeco-blue rounded-2xl px-4 py-3" data-testid="photo-investigate-note">
                              Please note: our team will investigate your image before providing a finalised quote.
                            </p>
                            <p className="mt-5 font-semibold text-neutral-700" data-testid="photo-onsite-enquiry-text">
                              Not 100% sure? Fill in our enquiry form — your photo comes with it and we'll confirm your exact swap-out.
                            </p>
                            {enquiryForm}
                          </>
                        ) : (
                          <div data-testid="photo-enquiry-block">
                            <p className="mt-4 font-semibold text-neutral-700" data-testid="photo-enquiry-script">
                              {identified
                                ? "We can provide a price for this unit, please call us or fill in our enquiry form below and we will quote you."
                                : "We are unable to identify this unit or it's data plate, please call us or fill in our enquiry form below and we will quote you."}
                            </p>
                            <a href="tel:0475776555" className="btn-brutal mt-4 inline-flex" data-testid="photo-enquiry-call-button">
                              <Phone className="w-5 h-5" strokeWidth={2.5} /> CALL 0475 776 555
                            </a>
                            {enquiryForm}
                          </div>
                        )}
                      </motion.div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-6">
                <div className="card-brutal p-6 !bg-tpeco-blue text-white" data-testid="photo-tips">
                  <h3 className="font-display uppercase text-3xl">HOT TIP</h3>
                  <ul className="mt-3 space-y-3 font-semibold text-sm">
                    <li className="flex gap-2"><ArrowRight className="w-4 h-4 mt-0.5 text-tpeco-pink shrink-0" /> Get close and keep the sticker in focus — the model number is gold.</li>
                    <li className="flex gap-2"><ArrowRight className="w-4 h-4 mt-0.5 text-tpeco-pink shrink-0" /> Whole-unit shot works too — shape tells us storage vs continuous flow.</li>
                    <li className="flex gap-2"><ArrowRight className="w-4 h-4 mt-0.5 text-tpeco-pink shrink-0" /> Prefer a human? Text the photo to 0475 776 555 and we'll ID it for you.</li>
                  </ul>
                </div>
                <div className="lg:hidden" data-testid="finder-video-mobile">
                  <SalesVideo />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
